import { useEffect, useState } from "react";
import { api, type AdminUser, type AdminRoom, type AdminInvite } from "../lib/api";

export function Admin() {
  const [tab, setTab] = useState<"users" | "rooms" | "invites">("users");
  return (
    <div style={{ padding: 16, overflow: "auto", height: "100%" }}>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <button className={tab === "users" ? "primary" : "ghost"} onClick={() => setTab("users")}>
          Users
        </button>
        <button className={tab === "rooms" ? "primary" : "ghost"} onClick={() => setTab("rooms")}>
          Rooms
        </button>
        <button className={tab === "invites" ? "primary" : "ghost"} onClick={() => setTab("invites")}>
          Invites
        </button>
      </div>
      {tab === "users" && <Users />}
      {tab === "rooms" && <Rooms />}
      {tab === "invites" && <Invites />}
    </div>
  );
}

function Users() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [admins, setAdmins] = useState<string[]>([]);
  const [transcriptsEnv, setTranscriptsEnv] = useState<string[]>([]);
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const load = () =>
    api
      .adminUsers()
      .then((r) => { setUsers(r.users); setAdmins(r.admins); setTranscriptsEnv(r.transcriptsUsers); })
      .catch((e) => setErr(e.message));
  useEffect(() => { load(); }, []);

  async function setGrant(id: string, grant: { transcripts?: boolean; admin?: boolean }) {
    setErr(null);
    try { await api.adminSetGrants(id, grant); load(); } catch (e) { setErr((e as Error).message); }
  }

  async function create() {
    setErr(null);
    try {
      await api.adminCreateUser(u.trim(), p);
      setU(""); setP(""); load();
    } catch (e) { setErr((e as Error).message); }
  }
  async function del(id: string, name: string) {
    if (!confirm(`Delete user "${name}"? Removes their room memberships; their messages stay (sender cleared).`)) return;
    try { await api.adminDeleteUser(id); load(); } catch (e) { setErr((e as Error).message); }
  }
  async function reset(id: string, name: string) {
    const np = prompt(`New password for "${name}" (min 8 chars):`);
    if (!np) return;
    try { await api.adminSetPassword(id, np); alert("Password updated."); } catch (e) { setErr((e as Error).message); }
  }

  return (
    <div>
      <h3 style={{ marginTop: 0 }}>Users ({users.length})</h3>
      <div style={{ display: "flex", gap: 6, marginBottom: 12, maxWidth: 520 }}>
        <input placeholder="username" value={u} onChange={(e) => setU(e.target.value)} />
        <input placeholder="password (8+)" type="password" value={p} onChange={(e) => setP(e.target.value)} />
        <button className="primary" onClick={create} disabled={!u.trim() || p.length < 8}>Add user</button>
      </div>
      {err && <p className="error">{err}</p>}
      <table className="admin-table">
        <thead>
          <tr><th>Username</th><th>Rooms</th><th>Transcripts</th><th>Admin</th><th>Created</th><th></th></tr>
        </thead>
        <tbody>
          {users.map((x) => {
            const envAdmin = admins.includes(x.username);
            const envTx = transcriptsEnv.includes(x.username);
            return (
              <tr key={x.id}>
                <td>{x.username}</td>
                <td>{x.rooms}</td>
                <td title={envTx ? "granted via env allowlist" : undefined}>
                  <input
                    type="checkbox"
                    checked={x.can_transcripts || envTx}
                    disabled={envTx}
                    onChange={(e) => setGrant(x.id, { transcripts: e.target.checked })}
                  />
                </td>
                <td title={envAdmin ? "granted via env allowlist" : undefined}>
                  <input
                    type="checkbox"
                    checked={x.is_admin || envAdmin}
                    disabled={envAdmin}
                    onChange={(e) => setGrant(x.id, { admin: e.target.checked })}
                  />
                </td>
                <td className="muted">{new Date(x.created_at).toLocaleDateString()}</td>
                <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                  <button className="linkbtn" onClick={() => reset(x.id, x.username)}>reset pw</button>{" "}
                  {!envAdmin && (
                    <button className="linkbtn" onClick={() => del(x.id, x.username)}>delete</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Rooms() {
  const [rooms, setRooms] = useState<AdminRoom[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const load = () => api.adminRooms().then((r) => setRooms(r.rooms)).catch((e) => setErr(e.message));
  useEffect(() => { load(); }, []);
  async function del(id: string, label: string | null) {
    if (!confirm(`Delete room "${label || id}"? All its messages and files are permanently deleted.`)) return;
    try { await api.adminDeleteRoom(id); load(); } catch (e) { setErr((e as Error).message); }
  }
  return (
    <div>
      <h3 style={{ marginTop: 0 }}>Rooms ({rooms.length})</h3>
      {err && <p className="error">{err}</p>}
      <table className="admin-table">
        <thead>
          <tr><th>Label</th><th>Mode</th><th>Creator</th><th>Members</th><th>Msgs</th><th>Created</th><th></th></tr>
        </thead>
        <tbody>
          {rooms.map((r) => (
            <tr key={r.id}>
              <td>
                {r.label || <span className="muted">(unlabeled)</span>}
                <div className="muted mono" style={{ fontSize: 10 }}>{r.id}</div>
              </td>
              <td>{r.mode}</td>
              <td>{r.created_by || <span className="muted">—</span>}</td>
              <td>{r.members}</td>
              <td>{r.messages}</td>
              <td className="muted">{new Date(r.created_at).toLocaleDateString()}</td>
              <td style={{ textAlign: "right" }}>
                <button className="linkbtn" onClick={() => del(r.id, r.label)}>delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Invites() {
  const [invites, setInvites] = useState<AdminInvite[]>([]);
  const [note, setNote] = useState("");
  const [count, setCount] = useState(1);
  const [minted, setMinted] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const load = () => api.adminInvites().then((r) => setInvites(r.invites)).catch((e) => setErr(e.message));
  useEffect(() => { load(); }, []);
  async function mint() {
    setErr(null);
    try {
      const r = await api.adminMintInvites(count, note.trim() || undefined);
      setMinted(r.codes); setNote(""); load();
    } catch (e) { setErr((e as Error).message); }
  }
  async function revoke(code: string) {
    if (!confirm("Revoke this invite code?")) return;
    try { await api.adminRevokeInvite(code); load(); } catch (e) { setErr((e as Error).message); }
  }
  const status = (i: AdminInvite) => (i.revoked_at ? "revoked" : i.used_by ? `used by ${i.used_by}` : "active");
  return (
    <div>
      <h3 style={{ marginTop: 0 }}>Invites</h3>
      <div style={{ display: "flex", gap: 6, marginBottom: 8, maxWidth: 520 }}>
        <input type="number" min={1} max={50} value={count} onChange={(e) => setCount(Number(e.target.value) || 1)} style={{ width: 70 }} />
        <input placeholder="note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
        <button className="primary" onClick={mint}>Mint</button>
      </div>
      {minted.length > 0 && (
        <div className="banner" style={{ marginBottom: 8 }}>
          New code(s) — copy now:
          <pre className="mono" style={{ margin: "4px 0 0", whiteSpace: "pre-wrap" }}>{minted.join("\n")}</pre>
        </div>
      )}
      {err && <p className="error">{err}</p>}
      <table className="admin-table">
        <thead><tr><th>Code</th><th>Status</th><th>Note</th><th></th></tr></thead>
        <tbody>
          {invites.map((i) => (
            <tr key={i.code}>
              <td className="mono">{i.code}</td>
              <td>{status(i)}</td>
              <td className="muted">{i.note || ""}</td>
              <td style={{ textAlign: "right" }}>
                {!i.revoked_at && !i.used_by && (
                  <button className="linkbtn" onClick={() => revoke(i.code)}>revoke</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
